import { EventEmitter } from 'events';
import { Viewer } from '../Viewer'
import * as THREE from 'three'
import { ViewerTool } from '../Tools/ViewerBaseTool'
import { Event } from 'three';
import { HSBControlsEventTypes } from '../Camera/HSBControls';

export enum PointerButtons {
    NONE = 0,
    PRIMARY = 1 << 0,
    SECONDARY = 1 << 1,
    AUXILIARY = 1 << 2,
    FOURTH = 1 << 3,
    FIFTH = 1 << 4,
    ALL = ~NONE,
}

export type ViewerPointerEvent = {
    type: 'down' | 'enter' | 'leave' | 'cancel' | 'up' | 'move' | 'zoomin' | 'zoomout' | 'dblclick';
    coords: THREE.Vector2;
    button: string;
    buttons: PointerButtons;
};
export type ToolEventType =
    | 'addIssue'
    | 'updateIssue'
    | 'selectIssue'
    | 'change'
    | 'deselect'
    | 'selection'
    | 'measurement'
    | 'showSelection'
    | 'hideSelection';
export type ToolEvent = { type: ToolEventType; data: any };


declare interface GenericEventEmitter<T extends string | symbol = string> extends EventEmitter{
    addListener(type: T, listener: (...args: any[]) => void): this;
    on(eventName: T, listener: (...args: any[]) => void): this;
    once(eventName: T, listener: (...args: any[]) => void): this;
    removeListener(eventName: T, listener: (...args: any[]) => void): this;
    off(eventName: T, listener: (...args: any[]) => void): this;
    removeAllListeners(event?: T): this;
    listeners(eventName: T): Function[];
    rawListeners(eventName: T): Function[];
    emit(eventName: T, ...args: any[]): boolean;
    listenerCount(eventName: T): number;
    prependListener(eventName: T, listener: (...args: any[]) => void): this;
    prependOnceListener(eventName: T, listener: (...args: any[]) => void): this;
}

/**
 * This handles all events related to the viewer.
 */
export class ViewerEvents {
    private domElement: HTMLElement;
    private mouse: THREE.Vector2;
    private _viewer: Viewer;
    private _buttons: PointerButtons = PointerButtons.NONE;
    public enabled: boolean;
    get currentTool(): ViewerTool | null {
        return this._viewer._currentTool;
    }

    private keys: Array<string>;
    private listeners: { event: string; listener: any }[] = [];
    public EntityClicked: EventEmitter;
    /**
     * Emits events for anything regarding the pointer (mouse/stylus/touch).
     * @property {'down' | 'enter' | 'leave' | 'cancel' | 'up' | 'move' | 'dblclick'} type The type of event
     * @property {THREE.Vector2} coords The mouse coords in screen space.
     * @property {string} button The button that changed in this event.
     * @property {PointerButtons} buttons The currently held buttons.
     */
    public PointerEvent: EventEmitter;
    /**
     * Emits events for any changes regarding the current active cameras.
     */
    public CameraEvent: GenericEventEmitter<HSBControlsEventTypes>;
    public VisibilityEvents: EventEmitter;
    public ToolEvents: EventEmitter;
    public ProjectEvents: EventEmitter;
    public RenderEvents: EventEmitter;

    constructor(domElement: HTMLCanvasElement, viewer: Viewer) {
        this.domElement = domElement;
        this._viewer = viewer;
        this.mouse = new THREE.Vector2();
        this.keys = [];
        this.enabled = true;

        domElement.addEventListener('pointerdown', this.OnPointerDown.bind(this), false);
        viewer._container.addEventListener('pointerenter', this.OnPointerEnter.bind(this), false);
        viewer._container.addEventListener('pointerleave', this.OnPointerLeave.bind(this), false);
        /* viewer._views.forEach(view => {
             view.addEventListener('pointerenter', this.OnPointerEnter.bind(this), false);
             view.addEventListener('pointerleave', this.OnPointerLeave.bind(this), false);
         });*/
        domElement.addEventListener('pointercancel', this.OnPointerCancel.bind(this), false);
        domElement.addEventListener('pointerup', this.OnPointerUp.bind(this), false);
        domElement.addEventListener('pointermove', this.OnPointerMove.bind(this), false);
        domElement.addEventListener('dblclick', this.OnDoubleClick.bind(this), false);
        window.addEventListener('keydown', this.OnKeyDown.bind(this), false);
        window.addEventListener('keyup', this.OnKeyUp.bind(this), false);
        domElement.addEventListener('wheel', this.OnMouseWheel.bind(this), false);

        this.EntityClicked = new EventEmitter();
        this.ToolEvents = new EventEmitter();
        this.ProjectEvents = new EventEmitter();
        this.PointerEvent = new EventEmitter();
        this.CameraEvent = new EventEmitter();
        this.VisibilityEvents = new EventEmitter();
        this.RenderEvents = new EventEmitter();
    }

    OnDestroy() {
        this.domElement.removeEventListener('pointerdown', this.OnPointerDown.bind(this), false);
        this._viewer._container.removeEventListener('pointerenter', this.OnPointerEnter.bind(this), false);
        this._viewer._container.removeEventListener('pointerleave', this.OnPointerLeave.bind(this), false);
        this.domElement.removeEventListener('pointercancel', this.OnPointerCancel.bind(this), false);
        this.domElement.removeEventListener('pointerup', this.OnPointerUp.bind(this), false);
        this.domElement.removeEventListener('pointermove', this.OnPointerMove.bind(this), false);
        this.domElement.removeEventListener('dblclick', this.OnDoubleClick.bind(this), false);
        window.removeEventListener('keydown', this.OnKeyDown.bind(this), false);
        window.removeEventListener('keyup', this.OnKeyUp.bind(this), false);
        this.domElement.removeEventListener('wheel', this.OnMouseWheel.bind(this), false);

        this.EntityClicked.removeAllListeners();
        this.ToolEvents.removeAllListeners();
        this.ProjectEvents.removeAllListeners();
        this.PointerEvent.removeAllListeners();
        this.CameraEvent.removeAllListeners();
        this.VisibilityEvents.removeAllListeners();
        this.RenderEvents.removeAllListeners();
    }

    stop() {
        this.enabled = false;
    }
    resume() {
        this.enabled = true;
    }

    ScreenToWorld(event: PointerEvent) {
        /**
         * Sets the coordinates relative to the domelement
         * [-1,1]__________________[1,1]
         *  |                        |
         *  |                        |
         *  |                        |
         *  |                        |
         * [-1,-1]_________________[1,-1]
         */
        let domrect = this.domElement.getBoundingClientRect();
        this.mouse.x = ((event.clientX - domrect.x) / domrect.width) * 2 - 1;
        this.mouse.y = -((event.clientY - domrect.y) / domrect.height) * 2 + 1;
    }

    getProperty<T, K extends keyof T>(obj: T, key: K) {
        return obj[key]; // Inferred type is T[K]
    }

    OnCameraEvent(event: any) {
        this.CameraEvent.emit(event.type, event);
    }



    /**
     * Invokes the current tools OnPointerDown method.
     * @todo At the moment pointer down does not get triggered when a second button is pressed if the first button is still down.
     * @param event
     */
    OnPointerDown(event: any) {
        if (!this.enabled) return;

        this.PointerEvent?.emit('down', { coords: this.mouse, button: PointerButtons[event.buttons], buttons: event.buttons });
        this._buttons = event.buttons;
        this.currentTool?.OnPointerDown(this.mouse, PointerButtons[event.buttons], event.buttons, event);
    }
    OnDoubleClick(event: MouseEvent) {
        if (!this.enabled) return;

        this.PointerEvent?.emit('dblclick', { coords: this.mouse, button: PointerButtons[event.buttons], buttons: event.buttons });
        this._buttons = event.buttons;
        this.currentTool?.OnDoubleClick(this.mouse, PointerButtons[event.buttons], event.buttons, event);
    }
    OnPointerEnter(event: any) {
        if (!this.enabled) return;
        this.PointerEvent.emit('enter', { coords: this.mouse, button: PointerButtons[event.buttons], buttons: event.buttons });
    }
    OnPointerLeave(event: any) {
        if (!this.enabled) return;
        this.PointerEvent?.emit('leave', { coords: this.mouse, button: PointerButtons[event.buttons], buttons: event.buttons });
    }
    OnPointerCancel(event: any) {
        if (!this.enabled) return;

        this.PointerEvent?.emit('cancel', { coords: this.mouse, button: PointerButtons[event.buttons], buttons: event.buttons });
    }
    OnPointerUp(event: any) {
        if (!this.enabled) return;

        this.PointerEvent?.emit('up', { coords: this.mouse, button: PointerButtons[this._buttons], buttons: event.buttons });
        if (!this._viewer.SceneManager.CameraThreshold)
            // Make sure that we're not in an extended camera movement
            this.currentTool?.OnPointerUp(
                this.mouse,
                PointerButtons[this._buttons & ~event.buttons],
                this._buttons & ~event.buttons,
                event
            );
        this._buttons = event.buttons;
    }
    OnPointerMove(event: PointerEvent) {
        if (!this.enabled) return;
        this.ScreenToWorld(event);
        if (event.buttons !== this._buttons) {
            // Extra button pressed
            if (event.buttons > this._buttons) {
                this.PointerEvent?.emit('down', {
                    coords: this.mouse,
                    button: PointerButtons[event.buttons & ~this._buttons],
                    buttons: event.buttons,
                });
                // this.currentTool?.OnPointerUp(this.mouse, PointerButtons[event.buttons & ~this._buttons], event.buttons & ~this._buttons,event);
                this._buttons = event.buttons;
            } else {
                // Button was released
                this.PointerEvent?.emit('up', {
                    coords: this.mouse,
                    button: PointerButtons[this._buttons & ~event.buttons],
                    buttons: event.buttons,
                });
                // this.currentTool?.OnPointerUp(this.mouse, PointerButtons[this._buttons & ~event.buttons], this._buttons & ~event.buttons,event);
                this._buttons = event.buttons;
            }
        }
        this.PointerEvent?.emit('move', { coords: this.mouse, button: PointerButtons[event.buttons], buttons: event.buttons });
        if (!this._viewer.SceneManager.CameraThreshold)
            // Make sure that we're not in an extended camera movement
            this.currentTool?.OnPointerMove(this.mouse, PointerButtons[event.buttons], event.buttons, event);
    }

    OnKeyDown(event: KeyboardEvent) {
        if (!this.enabled) return;
        if (this.keys.findIndex((key) => key === event.code) < 0) {
            this.keys.push(event.code);
            this.currentTool?.OnKeyPressed(event.code, event);
        }
        this.currentTool?.OnKeyDown(event.code, event);
    }

    OnKeyUp(event: KeyboardEvent) {
        if (!this.enabled) return;
        let index = this.keys.findIndex((key) => key === event.code);
        this.keys.splice(index);
        this.currentTool?.OnKeyReleased(event.code, event);
    }

    OnMouseWheel(event: WheelEvent) {
        this.PointerEvent?.emit(event.deltaY < 0 ? 'zoomin' : 'zoomout', {
            coords: this.mouse,
            button: 'AUXILIARY',
            buttons: event.buttons,
        });
    }

    onRenderFrame(event: Event) {
        this.RenderEvents?.emit('frame');
    }
}
